import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRightLeft,
  Boxes,
  ExternalLink,
  Link2,
  RefreshCw,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import { API, toast } from '@/App';
import DeliveryEstimateSummary from '@/components/shared/DeliveryEstimateSummary';
import SearchBar from '@/components/shared/SearchBar';
import ImportedOrderDetailDialog from '@/components/vendeur/ImportedOrderDetailDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { UI_NAV_LABELS } from '@/constants/uiLabels';
import {
  buildImportedOrderSearchText,
  formatImportedOrderDateTime,
  formatImportedOrderMoney,
  getImportedOrderConversionMeta,
  getImportedOrderPlatformMeta,
  getImportedOrderReadyForPickupMeta,
  getImportedOrderReference,
  getImportedOrderSelectedQuote,
  getImportedOrderStatusMeta,
  getImportedOrderTrackingCode,
} from '@/utils/importedOrders';

const PLATFORM_OPTIONS = [
  { value: 'all', label: 'Toutes les sources' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'woocommerce', label: 'WooCommerce' },
  { value: 'public_api', label: 'API publique' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'imported', label: 'Importee' },
  { value: 'awaiting_validation', label: 'A valider' },
  { value: 'ready_for_conversion', label: 'A convertir' },
  { value: 'converted', label: 'Convertie' },
  { value: 'tracking_active', label: 'Tracking actif' },
  { value: 'delivered', label: 'Livree' },
  { value: 'cancelled', label: 'Annulee' },
  { value: 'failed', label: 'Echec' },
];

const WORKFLOW_OPTIONS = [
  { value: 'all', label: 'Toutes les commandes' },
  { value: 'to_convert', label: 'A convertir' },
  { value: 'converted', label: 'Deja converties' },
];

const buildImportedOrdersErrorMessage = (error, fallbackMessage) => {
  const rawDetail = String(error?.response?.data?.detail || '').trim();
  if (!rawDetail) {
    return fallbackMessage;
  }
  if (rawDetail.startsWith('IMPORTED_ORDER_MISSING_FIELDS:')) {
    const fields = rawDetail.split(':')[1] || '';
    return `Conversion impossible. Champs manquants: ${fields.replace(/,/g, ', ')}`;
  }
  if (rawDetail === 'IMPORTED_ORDER_SELECTED_MODE_NOT_ELIGIBLE') {
    return 'Le mode de livraison selectionne n est plus disponible pour cette commande.';
  }
  if (rawDetail === 'IMPORTED_ORDER_NOT_FOUND') {
    return 'Cette commande importee est introuvable.';
  }
  return rawDetail;
};

const updateImportedOrderInCollection = (items, nextOrder) => {
  if (!Array.isArray(items) || !nextOrder?.id) {
    return items;
  }
  return items.map((item) => (item?.id === nextOrder.id ? { ...item, ...nextOrder } : item));
};

const ImportedOrdersPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [convertingOrderId, setConvertingOrderId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [workflowFilter, setWorkflowFilter] = useState('all');
  const [orders, setOrders] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadOrders = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (platformFilter !== 'all') {
        params.set('platform', platformFilter);
      }
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }

      const response = await axios.get(`${API}/integrations/imported-orders?${params.toString()}`);
      setOrders(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (error) {
      toast.error(buildImportedOrdersErrorMessage(error, 'Impossible de charger les commandes importees.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [platformFilter, statusFilter]);

  const filteredOrders = useMemo(() => {
    const normalizedQuery = String(searchQuery || '').trim().toLowerCase();

    return orders.filter((order) => {
      if (workflowFilter === 'to_convert' && order?.delivery_id) {
        return false;
      }
      if (workflowFilter === 'converted' && !order?.delivery_id) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      return buildImportedOrderSearchText(order).includes(normalizedQuery);
    });
  }, [orders, searchQuery, workflowFilter]);

  const summary = useMemo(() => {
    const items = filteredOrders;
    return {
      total: items.length,
      toConvert: items.filter((order) => !order?.delivery_id && order?.status === 'ready_for_conversion').length,
      converted: items.filter((order) => Boolean(order?.delivery_id)).length,
      trackingActive: items.filter((order) => Boolean(getImportedOrderTrackingCode(order))).length,
    };
  }, [filteredOrders]);

  const openOrderDetail = async (order) => {
    if (!order?.id) {
      return;
    }

    setDetailOpen(true);
    setSelectedOrder(order);
    setDetailLoading(true);
    setSyncLogs([]);

    try {
      const platform = String(order?.source_platform || 'public_api').trim().toLowerCase() || 'public_api';
      const [detailResponse, logsResponse] = await Promise.all([
        axios.get(`${API}/integrations/imported-orders/${order.id}`),
        axios.get(`${API}/integrations/sync-logs?platform=${encodeURIComponent(platform)}&limit=100`),
      ]);

      const nextOrder = detailResponse.data?.item || order;
      const nextLogs = Array.isArray(logsResponse.data?.items) ? logsResponse.data.items : [];

      setSelectedOrder(nextOrder);
      setOrders((current) => updateImportedOrderInCollection(current, nextOrder));
      setSyncLogs(nextLogs);
    } catch (error) {
      toast.error(buildImportedOrdersErrorMessage(error, 'Impossible de charger le detail de cette commande importee.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleConvertOrder = async (order) => {
    if (!order?.id) {
      return;
    }

    setConvertingOrderId(order.id);
    try {
      const response = await axios.post(`${API}/integrations/imported-orders/${order.id}/convert`, {});
      const payload = response.data?.data || {};
      const nextOrder = payload.imported_order || order;

      setOrders((current) => updateImportedOrderInCollection(current, nextOrder));
      if (selectedOrder?.id === order.id) {
        setSelectedOrder(nextOrder);
      }

      toast.success(
        payload.already_processed
          ? 'Cette commande etait deja convertie en livraison.'
          : 'Commande importee convertie en livraison Coli Noki.'
      );
    } catch (error) {
      toast.error(buildImportedOrdersErrorMessage(error, 'Impossible de convertir cette commande en livraison.'));
    } finally {
      setConvertingOrderId('');
    }
  };

  const handleViewDelivery = (order) => {
    if (!order?.delivery_id) {
      return;
    }
    navigate(`/vendeur/delivery/${order.delivery_id}`);
  };

  const handleViewTracking = (order) => {
    const trackingCode = getImportedOrderTrackingCode(order);
    if (!trackingCode) {
      return;
    }
    navigate(`/tracking?code=${encodeURIComponent(trackingCode)}`);
  };

  const handleCloseDetail = (nextOpen) => {
    setDetailOpen(nextOpen);
    if (!nextOpen) {
      setSyncLogs([]);
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="imported-orders-panel">
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-700">
            <Boxes className="h-4 w-4" />
            Operations e-commerce
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-950">{UI_NAV_LABELS.importedOrders}</h1>
            <p className="text-sm text-slate-600">
              Suivez les commandes venues de Shopify, WooCommerce et de l API publique, puis convertissez-les en livraisons Coli Noki.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="outline"
            className="border-slate-200"
            onClick={() => loadOrders({ silent: true })}
            disabled={refreshing}
            data-testid="imported-orders-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button asChild className="bg-red-500 hover:bg-red-600">
            <Link to="/vendeur/integrations">
              Gerer les connexions
              <ExternalLink className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border-t-4 border-t-slate-500">
          <CardHeader className="pb-2">
            <CardDescription>Total visible</CardDescription>
            <CardTitle className="text-3xl">{summary.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-t-4 border-t-blue-500">
          <CardHeader className="pb-2">
            <CardDescription>A convertir</CardDescription>
            <CardTitle className="text-3xl">{summary.toConvert}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-t-4 border-t-cyan-500">
          <CardHeader className="pb-2">
            <CardDescription>Livraisons creees</CardDescription>
            <CardTitle className="text-3xl">{summary.converted}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-t-4 border-t-emerald-500">
          <CardHeader className="pb-2">
            <CardDescription>Tracking actif</CardDescription>
            <CardTitle className="text-3xl">{summary.trackingActive}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <CardTitle className="text-xl">Liste operationnelle</CardTitle>
              <CardDescription>
                Les statuts, quotes, SLA et relations delivery affiches ici viennent uniquement des snapshots backend.
              </CardDescription>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Rechercher une commande importee"
                className="sm:col-span-2 xl:col-span-1"
              />

              <div className="space-y-1">
                <label htmlFor="imported-orders-platform-filter" className="text-xs font-medium text-slate-500">
                  Plateforme
                </label>
                <select
                  id="imported-orders-platform-filter"
                  data-testid="imported-orders-platform-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={platformFilter}
                  onChange={(event) => setPlatformFilter(event.target.value)}
                >
                  {PLATFORM_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label htmlFor="imported-orders-status-filter" className="text-xs font-medium text-slate-500">
                  Statut
                </label>
                <select
                  id="imported-orders-status-filter"
                  data-testid="imported-orders-status-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label htmlFor="imported-orders-workflow-filter" className="text-xs font-medium text-slate-500">
                  Workflow
                </label>
                <select
                  id="imported-orders-workflow-filter"
                  data-testid="imported-orders-workflow-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={workflowFilter}
                  onChange={(event) => setWorkflowFilter(event.target.value)}
                >
                  {WORKFLOW_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-slate-500">Chargement des commandes importees...</div>
          ) : filteredOrders.length === 0 ? (
            <div
              className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center"
              data-testid="imported-orders-empty-state"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm">
                <Boxes className="h-6 w-6 text-slate-500" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">Aucune commande importee a afficher</h3>
              <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600">
                Connectez votre boutique depuis la rubrique Integrations, puis laissez les imports alimenter cet espace operationnel.
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Button asChild variant="outline">
                  <Link to="/vendeur/integrations">Ouvrir Integrations</Link>
                </Button>
                {(searchQuery || platformFilter !== 'all' || statusFilter !== 'all' || workflowFilter !== 'all') ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setSearchQuery('');
                      setPlatformFilter('all');
                      setStatusFilter('all');
                      setWorkflowFilter('all');
                    }}
                  >
                    Reinitialiser les filtres
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => {
                const platformMeta = getImportedOrderPlatformMeta(order?.source_platform);
                const statusMeta = getImportedOrderStatusMeta(order?.status);
                const conversionMeta = getImportedOrderConversionMeta(order);
                const readyForPickupMeta = getImportedOrderReadyForPickupMeta(order);
                const selectedQuote = getImportedOrderSelectedQuote(order);
                const trackingCode = getImportedOrderTrackingCode(order);

                return (
                  <Card
                    key={order.id}
                    data-testid={`imported-order-card-${order.id}`}
                    className="overflow-hidden border-slate-200 shadow-sm"
                  >
                    <CardContent className="p-0">
                      <div className="flex flex-col gap-6 p-6 xl:flex-row xl:items-start xl:justify-between">
                        <div className="min-w-0 flex-1 space-y-5">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="space-y-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge className={platformMeta.className}>{platformMeta.label}</Badge>
                                <Badge className={statusMeta.className}>{statusMeta.label}</Badge>
                                <Badge className={conversionMeta.className}>{conversionMeta.label}</Badge>
                                <Badge className={readyForPickupMeta.className}>{readyForPickupMeta.label}</Badge>
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-slate-950">
                                  Commande externe {getImportedOrderReference(order)}
                                </h3>
                                <p className="text-sm text-slate-500">
                                  Creee le {formatImportedOrderDateTime(order?.created_at)}
                                </p>
                              </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Livraison Coli Noki</p>
                              <p className="mt-1 font-medium text-slate-900">{order?.delivery_id || 'Pas encore creee'}</p>
                              <p className="mt-1 text-xs">{trackingCode || 'Tracking en attente'}</p>
                            </div>
                          </div>

                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <div className="rounded-2xl border border-slate-200 p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client</p>
                              <p className="mt-2 font-medium text-slate-900">{order?.customer_name || '-'}</p>
                              <p className="mt-1 text-sm text-slate-500">{order?.customer_phone || order?.customer_email || 'Coordonnees indisponibles'}</p>
                            </div>

                            <div className="rounded-2xl border border-slate-200 p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Trajet</p>
                              <p className="mt-2 font-medium text-slate-900">
                                {order?.pickup_city || '-'} <ArrowRightLeft className="mx-1 inline h-4 w-4 text-slate-400" /> {order?.delivery_city || '-'}
                              </p>
                              <p className="mt-1 text-sm text-slate-500">{order?.delivery_address || 'Adresse de livraison a completer'}</p>
                              {(order?.shipping_reference || order?.delivery_reference) && (
                                <p className="mt-1 text-xs text-slate-500">
                                  Référence : {order.shipping_reference || order.delivery_reference}
                                </p>
                              )}
                            </div>

                            <div className="rounded-2xl border border-slate-200 p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Snapshot prix</p>
                              <p className="mt-2 font-medium text-slate-900">
                                {formatImportedOrderMoney(selectedQuote?.price_total, selectedQuote?.currency || 'FCFA')}
                              </p>
                              <p className="mt-1 text-sm text-slate-500">
                                Valeur declaree : {formatImportedOrderMoney(order?.declared_value, selectedQuote?.currency || 'FCFA')}
                              </p>
                            </div>

                            <div className="rounded-2xl border border-slate-200 p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Options</p>
                              <p className="mt-2 font-medium text-slate-900">{order?.selected_delivery_mode || 'standard'}</p>
                              <p className="mt-1 text-sm text-slate-500">
                                Assurance : {Boolean(order?.is_insured) ? 'Oui' : 'Non'}
                              </p>
                            </div>
                          </div>

                          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                            <div className="rounded-2xl border border-slate-200 p-4">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source & mapping</p>
                                  <p className="mt-1 text-sm text-slate-600">
                                    Source status : {order?.source_status || '-'} | Delivery cible : {order?.status_mapping?.delivery_status || '-'}
                                  </p>
                                </div>
                                <div className="text-right text-sm text-slate-500">
                                  <p>External ID</p>
                                  <p className="font-mono text-xs text-slate-700">{order?.external_order_id || '-'}</p>
                                </div>
                              </div>
                            </div>

                            <DeliveryEstimateSummary
                              source={order}
                              variant="compact"
                              testId={`imported-order-estimate-${order.id}`}
                              className="justify-self-start lg:justify-self-end"
                            />
                          </div>
                        </div>

                        <div className="flex w-full flex-col gap-3 xl:w-64">
                          <Button
                            type="button"
                            variant="outline"
                            className="justify-between"
                            data-testid={`imported-order-view-${order.id}`}
                            onClick={() => openOrderDetail(order)}
                          >
                            Voir le detail
                            <ExternalLink className="h-4 w-4" />
                          </Button>

                          {order?.delivery_id ? (
                            <Button
                              type="button"
                              className="justify-between bg-red-500 hover:bg-red-600"
                              data-testid={`imported-order-view-delivery-${order.id}`}
                              onClick={() => handleViewDelivery(order)}
                            >
                              Voir la livraison
                              <Truck className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              className="justify-between bg-red-500 hover:bg-red-600"
                              data-testid={`imported-order-convert-${order.id}`}
                              onClick={() => handleConvertOrder(order)}
                              disabled={convertingOrderId === order.id}
                            >
                              {convertingOrderId === order.id ? 'Conversion...' : 'Convertir en livraison'}
                              <Link2 className="h-4 w-4" />
                            </Button>
                          )}

                          {trackingCode ? (
                            <Button
                              type="button"
                              variant="outline"
                              className="justify-between"
                              data-testid={`imported-order-view-tracking-${order.id}`}
                              onClick={() => handleViewTracking(order)}
                            >
                              Voir le tracking
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          ) : null}

                          <Separator />

                          <div className="rounded-2xl border border-cyan-100 bg-cyan-50 p-4 text-sm text-cyan-900">
                            <div className="flex items-start gap-2">
                              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cyan-700" />
                              <div>
                                <p className="font-medium">Snapshot backend preserve</p>
                                <p className="mt-1 text-cyan-800">
                                  Prix, SLA et assurance affiches ici correspondent aux donnees snapshotees lors de l import.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ImportedOrderDetailDialog
        open={detailOpen}
        onOpenChange={handleCloseDetail}
        order={selectedOrder}
        logs={syncLogs}
        loading={detailLoading}
        converting={convertingOrderId === selectedOrder?.id}
        onConvert={handleConvertOrder}
        onViewDelivery={handleViewDelivery}
        onViewTracking={handleViewTracking}
      />
    </div>
  );
};

export default ImportedOrdersPanel;
